import React from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, Pencil } from 'lucide-react';
import { ApLedgerRecord, ApVendorDetailResponse, ApVendorRecord } from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import { formatApLedgerReference } from './apFormatters';
import { DrawerBadge, DrawerField } from '../shared/LedgerDrawerPrimitives';
import "react-datepicker/dist/react-datepicker.css";

const formatRecurringEvidenceSource = (value?: string | null) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return '—';
    if (normalized === 'bank') return 'Bank';
    if (normalized === 'bill') return 'Bill';
    if (normalized === 'hybrid') return 'Hybrid';
    return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

type DrawerPresentation = {
    title: string;
    subtitle: string;
    amountDisplay: React.ReactNode;
    metadataContent: React.ReactNode;
};

const getApParentBillLabel = (record?: ApLedgerRecord | null) => {
    if (!record) return '—';
    const parentLabel = String(record.parent_bill_label || '').trim();
    if (parentLabel) return parentLabel;
    const parentBillId = String(record.parent_bill_id || '').trim();
    if (parentBillId.includes('::ap_bill::')) {
        const suffix = parentBillId.split('::ap_bill::')[1]?.trim();
        return suffix ? `Bill #${suffix}` : 'Bill';
    }
    if (parentBillId.includes('::')) {
        return 'Bill';
    }
    return parentBillId || '—';
};

const ExpectedPaymentDateTrigger = React.forwardRef<
    HTMLSpanElement,
    {
        value?: string;
        onClick?: () => void;
        disabled?: boolean;
    }
>(({ value, onClick, disabled }, ref) => (
    <span
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={disabled ? undefined : onClick}
        onKeyDown={disabled ? undefined : (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
            }
        }}
        className={`flex items-center gap-1 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:text-indigo-600'} text-gray-700 transition-colors`}
    >
        <span>{value || '—'}</span>
        <Calendar size={14} className="text-gray-400" />
    </span>
));

ExpectedPaymentDateTrigger.displayName = 'ExpectedPaymentDateTrigger';

export const buildApVendorDrawerPresentation = ({
    vendor,
    vendorDetail,
    isLoading,
    isUpdatingRecurringOverride,
    formatDate,
    formatPercent,
    formatTitleCase,
    renderApBillLink,
    renderQboTransactionLink,
    renderBankReceiptLink,
    onEditRecurring,
    onDisableRecurring,
    onAddRecurring,
    onOpenAccountSelector,
    isUpdatingAccount,
    onExcludeRecurringEvidence,
}: {
    vendor: ApVendorRecord;
    vendorDetail?: ApVendorDetailResponse;
    isLoading: boolean;
    isUpdatingRecurringOverride?: boolean;
    formatDate: (value?: string) => string;
    formatPercent: (value?: number | null, decimals?: number) => string;
    formatTitleCase: (value?: string | null) => string;
    renderApBillLink: (billId?: string | null, label?: string | null) => React.ReactNode;
    renderQboTransactionLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    renderBankReceiptLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    onEditRecurring?: (stream: NonNullable<ApVendorDetailResponse['recurring_streams']>[number]) => void;
    onDisableRecurring?: (stream: NonNullable<ApVendorDetailResponse['recurring_streams']>[number]) => void;
    onAddRecurring?: () => void;
    onOpenAccountSelector?: () => void;
    isUpdatingAccount?: boolean;
    onExcludeRecurringEvidence?: (
        stream: NonNullable<ApVendorDetailResponse['recurring_streams']>[number],
        bankEvidence: NonNullable<NonNullable<ApVendorDetailResponse['recurring_streams']>[number]['bank_evidence_rows']>[number],
    ) => void;
}): DrawerPresentation => {
    const vendorSummary = vendorDetail?.summary;
    const vendorBills = vendorDetail?.bills || [];
    const vendorPayments = vendorDetail?.payments || [];
    const vendorBankDisbursements = vendorDetail?.bank_disbursements || [];
    const vendorBankHistory = vendorDetail?.bank_history || [];
    const vendorExceptions = vendorDetail?.exceptions || [];
    const vendorRecurringStreams = vendorDetail?.recurring_streams || [];
    const vendorObligations = vendorDetail?.obligations || [];
    const accountName = vendorDetail?.resolved_primary_accounting_account_name
        || vendor.primary_accounting_account_name
        || vendorDetail?.profile?.primary_accounting_account_name
        || '—';
    const accountCategory = vendorDetail?.resolved_primary_accounting_category
        || vendor.primary_accounting_category
        || vendorDetail?.profile?.primary_accounting_category
        || '—';
    const paymentMethodLabel = vendorDetail?.summary?.preferred_payment_method_label
        || vendor.preferred_payment_method_label
        || vendorDetail?.profile?.preferred_payment_method_label
        || 'Bank';
    const paymentMethodConfidence = vendorDetail?.summary?.preferred_payment_method_confidence
        ?? vendor.preferred_payment_method_confidence
        ?? vendorDetail?.profile?.preferred_payment_method_confidence
        ?? null;
    const paymentMethodSource = vendorDetail?.summary?.preferred_payment_method_source
        || vendor.preferred_payment_method_source
        || vendorDetail?.profile?.preferred_payment_method_source
        || null;
    const getRecurringBadgeLabel = (sourceMode?: string | null, overrideApplied?: boolean | null) => {
        const normalized = String(sourceMode || '').trim().toLowerCase();
        if (normalized === 'manual') return 'Manual';
        if (normalized === 'user_adjusted' || overrideApplied) return 'User adjusted';
        return 'Detected';
    };

    return {
        title: vendor?.vendor_name || 'Vendor',
        subtitle: 'AP Vendor',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(vendorSummary?.open_ap ?? vendor?.open_ap, 'USD')}
                </span>
                <DrawerBadge className="bg-amber-100 text-amber-800">AP Vendor</DrawerBadge>
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <DrawerField label="Bucket" value={accountCategory} />
                    <DrawerField
                        label="Chart of Account"
                        value={
                            <div className="flex items-center gap-2">
                                <span className="min-w-0 truncate">{accountName}</span>
                                {onOpenAccountSelector && (
                                    <button
                                        type="button"
                                        onClick={onOpenAccountSelector}
                                        disabled={isUpdatingAccount}
                                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                        title="Edit chart of account"
                                    >
                                        <Pencil size={13} />
                                    </button>
                                )}
                            </div>
                        }
                    />
                    <DrawerField label="Primary Frequency" value={vendorRecurringStreams[0]?.frequency_label || vendor.primary_frequency_label || '—'} />
                    <DrawerField
                        label="Pay By"
                        value={
                            <div>
                                <div>{paymentMethodLabel}{typeof paymentMethodConfidence === 'number' ? ` (${Math.round(paymentMethodConfidence * 100)}%)` : ''}</div>
                                {paymentMethodSource && <div className="mt-1 text-xs text-gray-400">Source: {paymentMethodSource.replace(/_/g, ' ')}</div>}
                            </div>
                        }
                    />
                    <DrawerField
                        label="Open Bills"
                        value={vendorSummary?.open_bill_count
                            ?? vendor?.open_bill_count
                            ?? ((typeof vendor?.open_ap === 'number' && vendor.open_ap > 0 && typeof vendor?.vendor_bill_count === 'number')
                                ? vendor.vendor_bill_count
                                : '—')}
                    />
                    <DrawerField
                        label="Total Billed"
                        value={typeof vendorSummary?.total_billed_amount === 'number'
                            ? formatCurrency(vendorSummary?.total_billed_amount, 'USD')
                            : typeof vendor?.total_billed_amount === 'number'
                                ? formatCurrency(vendor?.total_billed_amount, 'USD')
                                : typeof vendor?.open_bill_amount === 'number'
                                    ? formatCurrency(vendor?.open_bill_amount, 'USD')
                                    : '—'}
                    />
                    <DrawerField
                        label="Paid / Closed"
                        value={typeof vendorSummary?.paid_amount_total === 'number'
                            ? formatCurrency(vendorSummary?.paid_amount_total, 'USD')
                            : typeof vendor?.paid_amount_total === 'number'
                                ? formatCurrency(vendor?.paid_amount_total, 'USD')
                                : typeof vendor?.open_bill_amount === 'number'
                                    ? formatCurrency(
                                        Math.max(
                                            vendor.open_bill_amount - (typeof vendor?.open_ap === 'number' ? vendor.open_ap : vendor.open_bill_amount),
                                            0,
                                        ),
                                        'USD',
                                    )
                                    : '—'}
                    />
                    <DrawerField
                        label="Open AP"
                        value={typeof vendorSummary?.open_ap === 'number'
                            ? formatCurrency(vendorSummary?.open_ap, 'USD')
                            : typeof vendor?.open_ap === 'number'
                                ? formatCurrency(vendor?.open_ap, 'USD')
                                : typeof vendor?.open_bill_amount === 'number'
                                    ? formatCurrency(vendor?.open_bill_amount, 'USD')
                                    : '—'}
                    />
                    <DrawerField label="Overdue AP" value={typeof vendorSummary?.overdue_ap === 'number' ? formatCurrency(vendorSummary?.overdue_ap, 'USD') : (typeof vendor?.overdue_ap === 'number' ? formatCurrency(vendor?.overdue_ap, 'USD') : '—')} />
                    <DrawerField label="Last Bill" value={formatDate((vendorSummary?.last_bill_date as string | undefined) || vendor?.last_bill_date || undefined)} />
                    <DrawerField label="Last Payment" value={formatDate((vendorSummary?.last_payment_date as string | undefined) || vendor?.last_payment_date || undefined)} />
                    <DrawerField label="Concentration" value={formatPercent((vendorSummary?.concentration_share as number | null | undefined) ?? vendor?.concentration_share)} />
                </div>

                <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Vendor Activity</h3>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                        {isLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                Loading vendor activity...
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-medium text-slate-900">Recurring streams:</span>
                                        {onAddRecurring && (
                                            <button
                                                type="button"
                                                onClick={onAddRecurring}
                                                disabled={isUpdatingRecurringOverride}
                                                className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Add recurring
                                            </button>
                                        )}
                                    </div>
                                    {vendorRecurringStreams.length ? vendorRecurringStreams.map((stream, index) => (
                                        <div key={stream.ap_recurring_stream_id || `vendor-stream-${index}`} className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    {index + 1}. {(stream.frequency_label || formatTitleCase(stream.frequency || '')) || 'No frequency'} | {formatCurrency(stream.typical_amount, 'USD')} | Next {formatDate(stream.next_expected_payment_date || undefined)} | {(stream.accounting_category || 'Unclassified')} | {(stream.accounting_account_name || 'No account')}
                                                </div>
                                                <DrawerBadge className="bg-indigo-100 text-indigo-700">
                                                    {getRecurringBadgeLabel(stream.source_mode, stream.override_applied)}
                                                </DrawerBadge>
                                            </div>
                                            <div className="mt-1 text-xs text-slate-500">
                                                {formatRecurringEvidenceSource(stream.evidence_source)} evidence | Bills {stream.bill_evidence_count ?? 0} | Bank {stream.bank_evidence_count ?? 0}
                                            </div>
                                            {(onEditRecurring || onDisableRecurring) && (
                                                <div className="mt-2 flex items-center gap-2">
                                                    {onEditRecurring && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onEditRecurring(stream)}
                                                            disabled={isUpdatingRecurringOverride}
                                                            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                    {onDisableRecurring && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onDisableRecurring(stream)}
                                                            disabled={isUpdatingRecurringOverride}
                                                            className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            Disable
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {stream.bill_evidence_rows && stream.bill_evidence_rows.length > 0 && (
                                                <div className="mt-2 space-y-1">
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill Evidence</div>
                                                    {stream.bill_evidence_rows.map((billEvidence, evidenceIndex) => (
                                                        <div key={`${stream.ap_recurring_stream_id || index}-bill-${billEvidence.bill_id || evidenceIndex}`} className="text-xs text-slate-600">
                                                            {evidenceIndex + 1}. {renderApBillLink(
                                                                billEvidence.bill_id || undefined,
                                                                billEvidence.reference_label || billEvidence.reference_no || billEvidence.bill_id || 'Bill',
                                                            )} | Due {formatDate(billEvidence.due_date || billEvidence.bill_date || undefined)} | {formatCurrency(billEvidence.amount, 'USD')}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {stream.bank_evidence_rows && stream.bank_evidence_rows.length > 0 && (
                                                <div className="mt-2 space-y-1">
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bank Evidence</div>
                                                    {stream.bank_evidence_rows.map((bankEvidence, evidenceIndex) => (
                                                        <div key={`${stream.ap_recurring_stream_id || index}-bank-${bankEvidence.transaction_id || evidenceIndex}`} className="flex items-center justify-between gap-3 text-xs text-slate-600">
                                                            <div>
                                                                {evidenceIndex + 1}. {renderBankReceiptLink(
                                                                    bankEvidence.transaction_id || undefined,
                                                                    bankEvidence.vendor_name || bankEvidence.description || 'Bank transaction',
                                                                )} | {formatDate(bankEvidence.date || undefined)} | {formatCurrency(bankEvidence.amount, 'USD')}
                                                            </div>
                                                            {onExcludeRecurringEvidence && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onExcludeRecurringEvidence(stream, bankEvidence)}
                                                                    disabled={isUpdatingRecurringOverride}
                                                                    className="shrink-0 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                                >
                                                                    Exclude
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No recurring streams detected for this vendor.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Upcoming obligations:</span>
                                    {vendorObligations.length ? vendorObligations.map((obligation, index) => (
                                        <div key={obligation.ap_obligation_id || `vendor-obligation-${index}`} className="mt-1 text-sm text-slate-700">
                                            {index + 1}. {obligation.source_type === 'recurring_inferred' ? 'Expected' : 'Due'} {formatDate((obligation.source_type === 'recurring_inferred' ? obligation.expected_payment_date : obligation.due_date) || undefined)} | {formatCurrency(obligation.amount_due, 'USD')} | {(obligation.frequency_label || formatTitleCase(obligation.recurrence_type || '')) || 'No frequency'} | {(obligation.accounting_category || 'Unclassified')} | {(obligation.accounting_account_name || 'No account')} | {formatTitleCase(obligation.obligation_bucket || obligation.status || 'open')}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No upcoming obligations for this vendor.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Bills:</span>
                                    {vendorBills.length ? vendorBills.map((bill, index) => (
                                        <div key={bill.ap_bill_id || bill.source_id || `vendor-bill-${index}`} className="mt-1 text-sm text-slate-700">
                                            {index + 1}. {renderApBillLink(bill.source_id, bill.bill_number || bill.source_id || 'Bill')} | Due {formatDate(bill.due_date || undefined)} | Open {formatCurrency(bill.open_amount, 'USD')} | {formatTitleCase(bill.status || bill.qbo_status || 'open')}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No bills linked to this vendor yet.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Payments:</span>
                                    {vendorPayments.length ? vendorPayments.map((payment, index) => (
                                        <div key={payment.ap_payment_id || payment.source_id || `vendor-payment-${index}`} className="mt-1 text-sm text-slate-700">
                                            {index + 1}. {renderQboTransactionLink(payment.qbo_transaction_list_id || payment.source_id, payment.transaction_type || 'Payment')} | {formatDate(payment.payment_date || undefined)} | {formatCurrency(payment.payment_amount, 'USD')} | {payment.payment_account_name || 'No payment account'}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No QBO payments linked to this vendor yet.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Bank Disbursements:</span>
                                    {vendorBankDisbursements.length ? vendorBankDisbursements.map((disbursement, index) => (
                                        <div key={disbursement.ap_bank_disbursement_id || disbursement.source_id || `vendor-bank-${index}`} className="mt-1 text-sm text-slate-700">
                                            {index + 1}. {renderBankReceiptLink(disbursement.source_id, disbursement.counterparty_name || 'Bank disbursement')} | {formatDate(disbursement.disbursement_date || undefined)} | {formatCurrency(disbursement.disbursement_amount, 'USD')} | {formatTitleCase(disbursement.status || 'posted')}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No bank disbursements linked to this vendor yet.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Bank History:</span>
                                    {vendorBankHistory.length ? vendorBankHistory.map((transaction, index) => (
                                        <div key={transaction.transaction_id || transaction.source_id || `vendor-bank-history-${index}`} className="mt-1 text-sm text-slate-700">
                                            {index + 1}. {renderBankReceiptLink(
                                                transaction.transaction_id || transaction.source_id || undefined,
                                                transaction.vendor_name || transaction.counterparty_name || 'Bank transaction',
                                            )} | {formatDate(transaction.date || undefined)} | {formatCurrency(transaction.amount, 'USD')} | {transaction.normalized_description || transaction.cleaned_description || transaction.original_description || 'No description'}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No recent bank history found for this vendor.</div>
                                    )}
                                </div>

                                <div>
                                    <span className="font-medium text-slate-900">Open review items:</span>
                                    {vendorExceptions.length ? vendorExceptions.map((exception, index) => (
                                        <div key={exception.ap_exception_id || `vendor-exception-${index}`} className="mt-1 text-sm text-amber-800">
                                            {index + 1}. {String(exception.exception_type || '').trim() === 'bill_without_document' && exception.bill_source_id ? (
                                                <>
                                                    {renderApBillLink(
                                                        exception.bill_source_id,
                                                        exception.bill_reference_label
                                                        || exception.bill_reference_no
                                                        || exception.bill_id
                                                        || 'Bill',
                                                    )} | {formatCurrency(
                                                        typeof exception.bill_open_amount === 'number'
                                                            ? exception.bill_open_amount
                                                            : exception.bill_amount,
                                                        'USD',
                                                    )} | {exception.reason || 'Missing linked uploaded bill support'}
                                                </>
                                            ) : (
                                                <>
                                                    {formatTitleCase(exception.exception_type || 'exception')} | {exception.reason || 'Review required'}
                                                </>
                                            )}
                                        </div>
                                    )) : (
                                        <div className="mt-1 text-sm text-slate-500">No open AP review items for this vendor.</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        ),
    };
};

export const buildApLedgerDrawerPresentation = ({
    apLedgerRecord,
    formatDate,
    formatTitleCase,
    formatConfidencePercent,
    renderBankReceiptLink,
    onExpectedPaymentDateChange,
    isUpdatingExpectedPaymentDate,
}: {
    apLedgerRecord: ApLedgerRecord;
    formatDate: (value?: string) => string;
    formatTitleCase: (value?: string | null) => string;
    formatConfidencePercent: (value?: number | null) => string;
    renderBankReceiptLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    onExpectedPaymentDateChange?: (date: Date | null) => void;
    isUpdatingExpectedPaymentDate?: boolean;
}): DrawerPresentation => {
    const apDisplayType = apLedgerRecord.display_record_type || formatTitleCase(apLedgerRecord.record_type || 'AP record');
    const hasObligationDetails = Boolean(
        apLedgerRecord.obligation_source
        || apLedgerRecord.obligation_bucket
        || apLedgerRecord.forecast_method
        || apLedgerRecord.accounting_account_name
        || apLedgerRecord.accounting_category,
    );
    const isEditableExpectedPaymentDate = apLedgerRecord.record_type === 'recurring' || apLedgerRecord.obligation_source === 'ap_recurring';
    const showObligationContext = hasObligationDetails && apLedgerRecord.record_type !== 'bank_disbursement';
    const showClassificationFields = Boolean(apLedgerRecord.accounting_account_name || apLedgerRecord.accounting_category);
    const showForecastFields = apLedgerRecord.record_type === 'recurring' || apLedgerRecord.record_type === 'obligation';
    const showDueDate = apLedgerRecord.record_type !== 'bank_disbursement' && (
        apLedgerRecord.record_type === 'bill'
        || apLedgerRecord.record_type === 'bill_line'
        || apLedgerRecord.record_type === 'document'
        || apLedgerRecord.record_type === 'obligation'
        || Boolean(apLedgerRecord.due_date)
    );
    const showExpectedPaymentDate = apLedgerRecord.record_type === 'recurring';
    const showDateSource = showExpectedPaymentDate && Boolean(apLedgerRecord.date_source);
    const showReferenceField = apLedgerRecord.record_type !== 'recurring';
    const metadataFields: React.ReactNode[] = [
        <DrawerField key="vendor" label="Vendor" value={apLedgerRecord.vendor_name || '—'} />,
        <DrawerField key="operating-status" label="Operating Status" value={formatTitleCase(apLedgerRecord.operating_status || apLedgerRecord.status || 'open')} />,
        ...(showReferenceField ? [<DrawerField key="reference" label="Reference" value={formatApLedgerReference(apLedgerRecord)} />] : []),
        <DrawerField key="reconciliation" label="Reconciliation" value={formatTitleCase(apLedgerRecord.reconciliation_status || 'open')} />,
        <DrawerField key="event-date" label="Event Date" value={formatDate(apLedgerRecord.event_date)} />,
        ...(showDueDate ? [<DrawerField key="due-date" label="Due Date" value={formatDate(apLedgerRecord.due_date || undefined)} />] : []),
        ...(showExpectedPaymentDate ? [(
            <DrawerField
                key="expected-payment-date"
                label="Expected Payment Date"
                value={isEditableExpectedPaymentDate && onExpectedPaymentDateChange ? (
                    <div className="inline-block">
                        <DatePicker
                            selected={apLedgerRecord.expected_payment_date ? new Date(apLedgerRecord.expected_payment_date) : null}
                            onChange={(date: Date | null) => onExpectedPaymentDateChange(date)}
                            customInput={
                                <ExpectedPaymentDateTrigger
                                    value={formatDate(apLedgerRecord.expected_payment_date || undefined)}
                                    disabled={!!isUpdatingExpectedPaymentDate}
                                />
                            }
                            popperPlacement="bottom-end"
                        />
                    </div>
                ) : formatDate(apLedgerRecord.expected_payment_date || undefined)}
            />
        )] : []),
        ...(showDateSource ? [<DrawerField key="date-source" label="Date Source" value={formatTitleCase(apLedgerRecord.date_source || '—')} />] : []),
        ...((apLedgerRecord.record_type === 'recurring' && (apLedgerRecord.frequency_label || apLedgerRecord.recurrence_type))
            ? [<DrawerField key="frequency" label="Frequency" value={apLedgerRecord.frequency_label || formatTitleCase(apLedgerRecord.recurrence_type || '—')} />]
            : []),
        <DrawerField key="amount" label="Amount" value={formatCurrency(apLedgerRecord.amount, 'USD')} />,
        <DrawerField key="operating-open" label="Operating Open" value={formatCurrency(apLedgerRecord.operating_open_amount ?? apLedgerRecord.open_amount, 'USD')} />,
        ...(apLedgerRecord.qbo_status ? [<DrawerField key="qbo-status" label="QBO Status" value={formatTitleCase(apLedgerRecord.qbo_status || 'open')} />] : []),
        ...((apLedgerRecord.qbo_open_amount !== undefined && apLedgerRecord.qbo_open_amount !== null)
            ? [<DrawerField key="qbo-open" label="QBO Open" value={formatCurrency(apLedgerRecord.qbo_open_amount, 'USD')} />]
            : []),
        ...(showClassificationFields ? [<DrawerField key="bucket" label="Bucket" value={apLedgerRecord.accounting_category || '—'} />] : []),
        ...(showClassificationFields ? [<DrawerField key="chart-of-account" label="Chart of Account" value={apLedgerRecord.accounting_account_name || '—'} />] : []),
    ];
    return {
        title: apLedgerRecord.record_type === 'bill_line'
            ? (apLedgerRecord.line_description || getApParentBillLabel(apLedgerRecord) || 'Bill Line')
            : (formatApLedgerReference(apLedgerRecord) || apDisplayType),
        subtitle: apLedgerRecord.vendor_name || 'Vendor',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(apLedgerRecord.amount, 'USD')}
                </span>
                <DrawerBadge className="bg-blue-100 text-blue-800">AP Ledger</DrawerBadge>
                <DrawerBadge className="bg-slate-100 text-slate-700">{apDisplayType}</DrawerBadge>
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    {metadataFields}
                </div>
                {apLedgerRecord.record_type === 'bill_line' && (
                    <div className="grid grid-cols-2 gap-4">
                        <DrawerField label="Line #" value={apLedgerRecord.line_number ?? '—'} />
                        <DrawerField label="Description" value={apLedgerRecord.line_description || '—'} />
                        <DrawerField label="Quantity" value={apLedgerRecord.quantity ?? '—'} />
                        <DrawerField label="Unit Price" value={apLedgerRecord.unit_amount != null ? formatCurrency(apLedgerRecord.unit_amount, 'USD') : '—'} />
                        <DrawerField label="Parent Bill" value={getApParentBillLabel(apLedgerRecord)} />
                        <DrawerField label="Parent Due" value={formatDate(apLedgerRecord.parent_bill_due_date || undefined)} />
                        <DrawerField label="Parent Status" value={formatTitleCase(apLedgerRecord.parent_bill_status || '—')} />
                        <DrawerField label="Parent Open" value={apLedgerRecord.parent_bill_open_amount != null ? formatCurrency(apLedgerRecord.parent_bill_open_amount, 'USD') : '—'} />
                    </div>
                )}
                {showObligationContext && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        {apLedgerRecord.evidence_transactions && apLedgerRecord.evidence_transactions.length > 0 ? (
                            <div className="space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    {apLedgerRecord.record_type === 'recurring' ? 'Reference History' : 'Recent Supporting Transactions'}
                                </p>
                                <div className="space-y-2">
                                    {apLedgerRecord.evidence_transactions.map((transaction, index) => (
                                        <div key={`${transaction.transaction_id || 'tx'}-${index}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {renderBankReceiptLink(
                                                            transaction.transaction_id || undefined,
                                                            transaction.vendor_name || transaction.description || 'Bank transaction',
                                                        )}
                                                    </div>
                                                    <div className="mt-1 text-xs text-gray-500 truncate" title={transaction.description || undefined}>
                                                        {transaction.description || '—'}
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <div className="text-sm font-medium text-gray-900">{formatCurrency(transaction.amount ?? undefined, 'USD')}</div>
                                                    <div className="mt-1 text-xs text-gray-500">{formatDate(transaction.date || undefined)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {(apLedgerRecord.anchor_source_id || apLedgerRecord.reference_no) ? (
                                    <DrawerField
                                        label={apLedgerRecord.record_type === 'recurring' ? 'Reference Key' : 'Reference'}
                                        value={apLedgerRecord.anchor_source_id || apLedgerRecord.reference_no || '—'}
                                    />
                                ) : <div />}
                                <DrawerField label="Needs Review" value={apLedgerRecord.needs_llm_review ? 'Yes' : 'No'} />
                            </div>
                        )}
                    </div>
                )}
                {(apLedgerRecord.qbo_status || apLedgerRecord.qbo_open_amount !== undefined) &&
                    (String(apLedgerRecord.operating_status || apLedgerRecord.status || '').trim().toLowerCase() !== String(apLedgerRecord.qbo_status || '').trim().toLowerCase()
                        || Number(apLedgerRecord.operating_open_amount ?? apLedgerRecord.open_amount ?? 0) !== Number(apLedgerRecord.qbo_open_amount ?? apLedgerRecord.open_amount ?? 0)) && (
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                            Finz is using the operating payment trail for AP, while QBO still shows this bill as open. This usually means the payment was detected or confirmed before QBO finished closing the bill.
                        </div>
                    )}
                {apLedgerRecord.reason && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        {apLedgerRecord.reason}
                    </div>
                )}
            </div>
        ),
    };
};
